import { FC, FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Checkbox, ChevronDownOutline, ChevronUpOutline, Flex, HStack, Input, Spinner, Text, useToast, VStack } from '@bonusly/design-system';

import { useDebounceValue } from '../../hooks/use-debounce-value';
import { useGithubSearchRepos, useGithubUpdatePreferences } from '../../queries/github';
import { GithubRepo } from '../../schemas/github';

interface GithubScopePickerProps {
	selectedOrgs: string[];
	selectedRepos: string[];
}

export const GithubScopePicker: FC<GithubScopePickerProps> = ({ selectedOrgs: initialOrgs, selectedRepos: initialRepos }) => {
	const { t } = useTranslation('bizy_settings', { keyPrefix: 'github.scopePicker' });
	const [savedOrgs, setSavedOrgs] = useState<string[]>(initialOrgs);
	const [savedRepos, setSavedRepos] = useState<string[]>(initialRepos);
	const [selectedOrgs, setSelectedOrgs] = useState<string[]>(initialOrgs);
	const [selectedRepos, setSelectedRepos] = useState<string[]>(initialRepos);
	const [searchQuery, setSearchQuery] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const [isExpanded, setIsExpanded] = useState(() => initialOrgs.length === 0 && initialRepos.length === 0);
	const debouncedQuery = useDebounceValue(searchQuery, 300);
	const toast = useToast();
	const inputRef = useRef<HTMLInputElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setSavedOrgs(initialOrgs);
		setSelectedOrgs(initialOrgs);
	}, [initialOrgs]);

	useEffect(() => {
		setSavedRepos(initialRepos);
		setSelectedRepos(initialRepos);
	}, [initialRepos]);

	const { data: searchResults, isLoading: isSearching } = useGithubSearchRepos(debouncedQuery);
	const { mutateAsync: updatePreferences, isLoading: isSaving } = useGithubUpdatePreferences();

	const isDirty = useMemo(() => {
		if (savedOrgs.length !== selectedOrgs.length || savedRepos.length !== selectedRepos.length) return true;
		return !savedOrgs.every((o) => selectedOrgs.includes(o)) || !savedRepos.every((r) => selectedRepos.includes(r));
	}, [savedOrgs, savedRepos, selectedOrgs, selectedRepos]);

	const addRepo = useCallback(
		(repo: GithubRepo) => {
			if (!selectedRepos.includes(repo.fullName)) {
				setSelectedRepos((prev) => [...prev, repo.fullName]);
			}
		},
		[selectedRepos]
	);

	const addOrg = useCallback(
		(owner: string) => {
			if (!selectedOrgs.includes(owner)) {
				setSelectedOrgs((prev) => [...prev, owner]);
			}
		},
		[selectedOrgs]
	);

	const removeRepo = useCallback((fullName: string) => {
		setSelectedRepos((prev) => prev.filter((r) => r !== fullName));
	}, []);

	const removeOrg = useCallback((login: string) => {
		setSelectedOrgs((prev) => prev.filter((o) => o !== login));
	}, []);

	const toggleRepo = useCallback(
		(repo: GithubRepo) => {
			if (selectedRepos.includes(repo.fullName)) {
				removeRepo(repo.fullName);
			} else {
				addRepo(repo);
			}
			inputRef.current?.focus();
		},
		[addRepo, removeRepo, selectedRepos]
	);

	const toggleOrg = useCallback(
		(owner: string) => {
			if (selectedOrgs.includes(owner)) {
				removeOrg(owner);
			} else {
				addOrg(owner);
			}
			inputRef.current?.focus();
		},
		[addOrg, removeOrg, selectedOrgs]
	);

	const handleSave = useCallback(async () => {
		try {
			const preferences = await updatePreferences({
				selected_orgs: selectedOrgs,
				selected_repos: selectedRepos,
			});
			setSavedOrgs(preferences.selectedOrgs);
			setSavedRepos(preferences.selectedRepos);
			setSelectedOrgs(preferences.selectedOrgs);
			setSelectedRepos(preferences.selectedRepos);
		} catch {
			toast({ title: t('saveError'), status: 'error', duration: 3000 });
		}
	}, [selectedOrgs, selectedRepos, t, updatePreferences, toast]);

	const hasSelections = selectedOrgs.length > 0 || selectedRepos.length > 0;
	const hasSavedAccess = savedOrgs.length > 0 || savedRepos.length > 0;
	const isQueryInSync = searchQuery === debouncedQuery;
	const isDebouncedSearchActive = debouncedQuery.length >= 2;
	const canShowSearchResults = isQueryInSync && isDebouncedSearchActive;

	const filteredResults = useMemo(() => {
		if (!canShowSearchResults || !searchResults?.repos) return [];
		return searchResults.repos.filter((r) => !selectedOrgs.includes(r.owner));
	}, [canShowSearchResults, searchResults, selectedOrgs]);

	const resultOrgs = useMemo(() => {
		const q = debouncedQuery.toLowerCase();
		const orgOwners = new Set(filteredResults.filter((r) => r.ownerType === 'Organization').map((r) => r.owner));
		return Array.from(orgOwners).filter((o) => o.toLowerCase().includes(q));
	}, [filteredResults, debouncedQuery]);

	const hasDropdownResults = resultOrgs.length > 0 || filteredResults.length > 0;
	const showDropdown = isOpen && searchQuery.length > 0;
	const isSearchPending = searchQuery.length >= 2 && (isSearching || !isQueryInSync);

	const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
		const next = event.relatedTarget as Node | null;
		if (next && popoverRef.current?.contains(next)) return;
		setIsOpen(false);
	};

	useEffect(() => {
		if (!hasSavedAccess) setIsExpanded(true);
	}, [hasSavedAccess]);

	const handleToggleExpanded = useCallback(() => {
		setIsExpanded((current) => !current);
	}, []);

	return (
		<Box bg="bg.canvas" borderRadius="lg" mt={0} p={4}>
			<Button
				aria-expanded={isExpanded}
				colorScheme="secondary"
				justifyContent="space-between"
				onClick={handleToggleExpanded}
				rightIcon={isExpanded ? <ChevronUpOutline size="xs" /> : <ChevronDownOutline size="xs" />}
				size="md"
				variant="ghost"
				w="full"
				px={0}
				_hover={{ bg: 'transparent' }}
			>
				<Box textAlign="left">
					<Text size="md" variant="strong" color="text.default">
						{t('title')}
					</Text>
					<Text size="sm" color="text.softer">
						{hasSelections ? t('descriptionWithSelections') : t('descriptionEmpty')}
					</Text>
				</Box>
			</Button>

			{isExpanded && (
				<Box pt={3}>
					{hasSelections && (
						<VStack align="stretch" bg="bg.surface" border="1px solid" borderColor="border.soft" borderRadius="md" mb={3} spacing={0}>
							{selectedOrgs.map((org) => (
								<Flex key={`org:${org}`} align="center" borderBottom="1px solid" borderColor="border.soft" gap={3} justify="space-between" px={3} py={2}>
									<Text color="text.default" size="sm" wordBreak="break-all">
										{t('selectedOrg', { org })}
									</Text>
									<Button colorScheme="secondary" flexShrink={0} onClick={() => removeOrg(org)} size="xs" variant="ghost">
										{t('remove')}
									</Button>
								</Flex>
							))}
							{selectedRepos.map((repo, index) => (
								<Flex
									key={`repo:${repo}`}
									align="center"
									borderBottom={index === selectedRepos.length - 1 ? '0' : '1px solid'}
									borderColor="border.soft"
									gap={3}
									justify="space-between"
									px={3}
									py={2}
								>
									<Text color="text.default" size="sm" wordBreak="break-all">
										repo:{repo}
									</Text>
									<Button colorScheme="secondary" flexShrink={0} onClick={() => removeRepo(repo)} size="xs" variant="ghost">
										{t('remove')}
									</Button>
								</Flex>
							))}
						</VStack>
					)}

					<Box position="relative">
						<Flex align="center" gap={2}>
							<Input
								ref={inputRef}
								aria-expanded={isOpen}
								flex={1}
								minW={0}
								bg="bg.surface"
								border="1px solid"
								borderColor="border.default"
								borderRadius="md"
								_focus={{ borderColor: 'border.focus', boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)' }}
								onBlur={handleInputBlur}
								onChange={(event) => setSearchQuery(event.target.value)}
								onFocus={() => setIsOpen(true)}
								placeholder={t('searchPlaceholder')}
								role="combobox"
								size="md"
								value={searchQuery}
								_placeholder={{ color: 'text.softest' }}
							/>
							{isSearchPending && <Spinner flexShrink={0} size="xs" />}
						</Flex>

						{showDropdown && (
							<Box
								ref={popoverRef}
								bg="bg.surface"
								border="1px solid"
								borderColor="border.soft"
								borderRadius="md"
								boxShadow="sm"
								maxH="160px"
								mt={1}
								overflowY="auto"
								onMouseDown={(event) => event.preventDefault()}
							>
								{searchQuery.length > 0 && searchQuery.length < 2 && (
									<Box px={3} py={2}>
										<Text color="text.softer" size="sm">
											{t('searchMinChars')}
										</Text>
									</Box>
								)}

								{canShowSearchResults && !isSearching && !hasDropdownResults && (
									<Box px={3} py={2}>
										<Text color="text.softer" size="sm">
											{t('noReposFound')}
										</Text>
									</Box>
								)}

								{canShowSearchResults && hasDropdownResults && (
									<VStack align="stretch" spacing={0}>
										{resultOrgs.map((owner) => (
											<Checkbox key={`org-add:${owner}`} isChecked={selectedOrgs.includes(owner)} onChange={() => toggleOrg(owner)} px={3} py={2}>
												<Text size="sm" variant="strong" wordBreak="break-all">
													{t('orgEntire', { org: owner })}
												</Text>
											</Checkbox>
										))}
										{filteredResults.map((repo) => (
											<Checkbox key={repo.fullName} isChecked={selectedRepos.includes(repo.fullName)} onChange={() => toggleRepo(repo)} px={3} py={2}>
												<HStack spacing={1}>
													<Text size="sm" wordBreak="break-all">
														repo:{repo.fullName}
													</Text>
													{repo.private && (
														<Text color="text.softer" flexShrink={0} size="sm">
															{t('private')}
														</Text>
													)}
												</HStack>
											</Checkbox>
										))}
									</VStack>
								)}
							</Box>
						)}
					</Box>
				</Box>
			)}

			{isDirty && (
				<Button colorScheme="primary" isLoading={isSaving} mt={3} onClick={handleSave} size="md">
					{t('save')}
				</Button>
			)}
		</Box>
	);
};
